import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, FileDown, AlertCircle } from 'lucide-react';
import { exportSummaryReportToPDF } from '../../utils/pdfExport';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';

const SeatBookingReport = () => {
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState({ total: 0, booked: 0, available: 0, totalRevenue: 0 });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchReport();
    fetchCollegeInfo();
  }, [currentPage, itemsPerPage]);

  const fetchCollegeInfo = async () => {
    try {
      const response = await apiClient.get('/api/tenants/my-college/settings');
      setCollegeInfo(response.data);
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      const response = await apiClient.get(`/api/seat-booking/?${params.toString()}`);
      
      // Handle both old (array) and new (paginated) response
      let bookingsData = [];
      let total = 0;
      let stats = { total: 0, booked: 0, available: 0, totalRevenue: 0 };
      
      if (Array.isArray(response.data)) {
        bookingsData = response.data;
        total = response.data.length;
        const booked = response.data.filter(b => b.status === 'Booked').length;
        const totalRevenue = response.data.reduce((sum, b) => sum + (b.booking_amount || b.amount || 0), 0);
        stats = { total, booked, available: total - booked, totalRevenue };
      } else {
        bookingsData = response.data?.data || [];
        total = response.data?.total || 0;
        stats = {
          total: response.data?.stats?.total || total,
          booked: response.data?.stats?.booked || 0,
          available: response.data?.stats?.available || 0,
          totalRevenue: response.data?.stats?.total_amount || 0
        };
      }
      
      setBookings(bookingsData);
      setTotalItems(total);
      setSummary(stats);
    } catch (err) {
      console.error('Error fetching seat bookings:', err);
      setError('Failed to load seat booking data. Please try again.');
      setBookings([]);
      setSummary({ total: 0, booked: 0, available: 0, totalRevenue: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const [exporting, setExporting] = useState(false);

  const exportToPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Fetch all data for export (without pagination limit)
      const params = new URLSearchParams();
      params.append('limit', '1000');
      
      const response = await apiClient.get(`/api/seat-booking/?${params.toString()}`);
      
      let allBookings = [];
      let totalRevenue = 0;
      
      if (Array.isArray(response.data)) {
        allBookings = response.data;
        totalRevenue = response.data.reduce((sum, b) => sum + (b.booking_amount || b.amount || 0), 0);
      } else {
        allBookings = response.data?.data || [];
        totalRevenue = response.data?.stats?.total_amount || allBookings.reduce((sum, b) => sum + (b.booking_amount || b.amount || 0), 0);
      }
      
      if (allBookings.length === 0) {
        alert('No data to export');
        return;
      }
      
      const booked = allBookings.filter(b => b.status === 'Booked').length;
      
      const stats = {
        'Total Bookings': allBookings.length,
        'Booked': booked,
        'Available': allBookings.length - booked,
        'Total Revenue': `Rs. ${totalRevenue.toFixed(2)}`
      };

      const columns = [
        { header: 'Booking ID', dataKey: 'booking_id' },
        { header: 'Student Name', dataKey: 'student_name' },
        { header: 'Seat Number', dataKey: 'seat_number' },
        { header: 'Booking Date', dataKey: 'booking_date' },
        { header: 'Amount', dataKey: 'amount' },
        { header: 'Status', dataKey: 'status' },
        { header: 'Entry User', dataKey: 'entry_user' }
      ];

      const data = allBookings.map(b => ({
        booking_id: b.booking_id || b.id || 'N/A',
        student_name: b.student_name || 'N/A',
        seat_number: b.seat_number || 'N/A',
        booking_date: b.booking_date || 'N/A',
        amount: `Rs. ${(b.booking_amount || b.amount || 0).toLocaleString('en-IN')}`,
        status: b.status || 'N/A',
        entry_user: b.created_by_name || '-'
      }));

      exportSummaryReportToPDF('Seat Booking Report', stats, columns, data, 'seat_booking_report.pdf', collegeInfo);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Seat Booking Report</CardTitle>
          <div className="flex gap-4 items-end">
            <div><Label>Start Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
            <div><Label>End Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
            <Button onClick={fetchReport} disabled={loading}><FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}</Button>
            <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700" disabled={loading || !bookings || bookings.length === 0 || exporting}>
              <FileDown className="w-4 h-4 mr-2" />Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchReport} className="ml-auto">Retry</Button>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Bookings</p>
              <p className="text-2xl font-bold text-blue-600">{summary.total}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Booked</p>
              <p className="text-2xl font-bold text-green-600">{summary.booked}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-gray-600">Available</p>
              <p className="text-2xl font-bold text-yellow-600">{summary.available}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-purple-600">₹{summary.totalRevenue.toFixed(2)}</p>
            </div>
          </div>

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Booking ID</th>
                <th className="text-left p-3">Receipt No</th>
                <th className="text-left p-3">Student Name</th>
                <th className="text-left p-3">Seat Number</th>
                <th className="text-left p-3">Booking Date</th>
                <th className="text-left p-3">Amount</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Entry User</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center p-8 text-gray-500">Loading...</td></tr>
              ) : bookings.length === 0 ? (
                <tr><td colSpan="8" className="text-center p-8 text-gray-500">No bookings found</td></tr>
              ) : (
                bookings.map((booking, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium text-blue-600">{booking.booking_id || 'N/A'}</td>
                    <td className="p-3 font-medium text-purple-600">{booking.receipt_no || 'N/A'}</td>
                    <td className="p-3">{booking.student_name}</td>
                    <td className="p-3">{booking.seat_number || 'N/A'}</td>
                    <td className="p-3">{booking.booking_date}</td>
                    <td className="p-3">₹{(booking.booking_amount || booking.amount || 0).toLocaleString('en-IN')}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${booking.status === 'Booked' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-gray-600">{booking.created_by_name || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SeatBookingReport;
